import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  findCategoryBySlug,
  helpArticlePath,
  helpCategoryPath,
  helpHomePath,
} from "../helpRoutes";

const CATEGORY_ICONS = {
  "Getting Started": "◎",
  Editor: "✦",
  Billing: "◇",
  Export: "↗",
  Troubleshooting: "!",
};

const categoryIcon = (name = "") => CATEGORY_ICONS[name] || "◎";

const PageContentOverView = ({
  selectedHeading,
  helpPageData,
  setIsSearchOpen,
  setSearchQuery,
  loading,
  isCategoryView,
  categorySlug,
  openSubheadingId,
  toggleSubheading,
}) => {
  const [homeSearchValue, setHomeSearchValue] = useState("");
  const categories = helpPageData?.length ? helpPageData : selectedHeading ? [selectedHeading] : [];
  const categoryHeading =
    isCategoryView && categorySlug
      ? findCategoryBySlug(categories, categorySlug) || selectedHeading
      : null;
  const openSub = categoryHeading?.subHeadings?.find(
    (sub) => `${categoryHeading._id}-${sub._id}` === openSubheadingId
  );

  if (isCategoryView && categoryHeading) {
    const subHeadings = categoryHeading.subHeadings || [];
    const tertiaries = openSub?.tertiarySubHeadings || [];

    return (
      <>
        <nav className="breadcrumbs" aria-label="Breadcrumb">
          <Link to={helpHomePath}>Help</Link>
          <span aria-hidden="true">/</span>
          {openSub ? (
            <>
              <Link to={helpCategoryPath(categoryHeading.heading)}>{categoryHeading.heading}</Link>
              <span aria-hidden="true">/</span>
              <span aria-current="page">{openSub.subHeading}</span>
            </>
          ) : (
            <span aria-current="page">{categoryHeading.heading}</span>
          )}
        </nav>
        <header className="article-header">
          <span className="eyebrow">{openSub ? categoryHeading.heading : "Help"}</span>
          <h1>{openSub ? openSub.subHeading : categoryHeading.heading}</h1>
          <p className="lede">
            {openSub
              ? `${tertiaries.length} guides in ${openSub.subHeading.toLowerCase()}.`
              : `${subHeadings.length} sections in ${categoryHeading.heading.toLowerCase()}.`}
          </p>
        </header>
        <section className="home-grid" aria-label={`${categoryHeading.heading} topics`}>
          {openSub
            ? tertiaries.map((tertiary) => (
                <Link
                  className="home-card"
                  to={helpArticlePath(
                    categoryHeading.heading,
                    openSub.subHeading,
                    tertiary.tertiarySubHeading,
                    tertiary._id
                  )}
                  key={tertiary._id}
                >
                  <span className="home-card-icon" aria-hidden="true">
                    {categoryIcon(categoryHeading.heading)}
                  </span>
                  <h2>{tertiary.tertiarySubHeading}</h2>
                  <p>{openSub.subHeading}</p>
                </Link>
              ))
            : subHeadings.map((sub) => (
                <button
                  className="home-card"
                  type="button"
                  key={sub._id}
                  onClick={() => toggleSubheading?.(categoryHeading._id, sub._id)}
                >
                  <span className="home-card-icon" aria-hidden="true">
                    {categoryIcon(categoryHeading.heading)}
                  </span>
                  <h2>{sub.subHeading}</h2>
                  <p>{(sub.tertiarySubHeadings || []).length} guides</p>
                </button>
              ))}
        </section>
      </>
    );
  }

  return (
    <>
      <section className="home-hero">
        <span className="eyebrow">ScrollX Help Center</span>
        <h1>Create better credits, faster</h1>
        <p className="lede">
          Find clear steps for projects, editing, rendering, billing, and troubleshooting.
        </p>
        <form
          className="home-search"
          onSubmit={(event) => {
            event.preventDefault();
            setSearchQuery?.(homeSearchValue);
            setIsSearchOpen?.(true);
          }}
        >
          <label className="sr-only" htmlFor="homeSearchInput">Search help</label>
          <input
            id="homeSearchInput"
            type="search"
            placeholder="What do you need help with?"
            autoComplete="off"
            value={homeSearchValue}
            onChange={(event) => setHomeSearchValue(event.target.value)}
          />
          <button type="submit" aria-label="Search">⌕</button>
        </form>
      </section>

      <section className="quick-tutorial" aria-labelledby="quickTutorialTitle">
        <div className="tutorial-heading">
          <h2 id="quickTutorialTitle">Quick tutorial</h2>
        </div>
        <div className="video-frame">
          <iframe
            src="https://www.youtube-nocookie.com/embed/q7ytl6vW9kI?rel=0"
            title="Create end credits in minutes using ScrollX"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      </section>

      <section className="home-grid" aria-label="Help categories">
        {loading && !categories.length ? (
          <div className="empty">Loading help topics…</div>
        ) : null}
        {categories.map((heading) => {
          const count = (heading.subHeadings || []).length;
          return (
            <Link
              className="home-card"
              to={helpCategoryPath(heading.heading)}
              key={heading._id}
            >
              <span className="home-card-icon" aria-hidden="true">
                {categoryIcon(heading.heading)}
              </span>
              <h2>{heading.heading}</h2>
              <p>{count} sections in {heading.heading.toLowerCase()}.</p>
            </Link>
          );
        })}
      </section>
    </>
  );
};

export default PageContentOverView;
